/**
 * AgentenChatTab — Chat-Interface zur Kommunikation mit Agenten
 *
 * Phase 1 (Issue #112):
 * - Agenten-Sidebar mit Status-Ampeln (grün/gelb/grau)
 * - Chat-Fenster: User-Anweisungen + Agenten-Protokoll-Einträge chronologisch
 * - Eingabefeld: Neue Anweisung an ausgewählten Agenten senden
 * - "Als Vorschlag speichern"-Aktion auf Agenten-Nachrichten
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Loader2, Bot, User, Clock, Zap, AlertCircle,
  CheckCircle2, XCircle, RefreshCw, ChevronRight, Lightbulb,
  CircleDot,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { AgentBriefing, ProtokollEintrag } from '@/hooks/useAgentenSystem';

// ─── CI Design Tokens (lokal kopiert für Unabhängigkeit) ──────────────────────

const CI = {
  red: '#C41E3A',
  redDark: '#9B1C1C',
  redLight: '#FEF2F2',
  redBorder: '#FECACA',
  bgPanel: '#FFFFFF',
  bgSection: '#F9FAFB',
  bgZebraOdd: '#F8F9FB',
  bgHover: '#F3F4F6',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#9CA3AF',
  textLight: '#D1D5DB',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  shadowHover: '0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
  emerald: '#059669',
  emeraldBg: '#ECFDF5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  blue: '#2563EB',
  blueBg: '#EFF6FF',
} as const;

// ─── Typen ────────────────────────────────────────────────────────────────────

interface ChatNachricht {
  id: string;
  typ: 'user' | 'agent';
  inhalt: string;
  zeitstempel: string;
  aktion?: string;
  erfolgreich?: boolean;
  // nur für agent-Nachrichten relevant
  protokoll_id?: string;
}

interface AgentMitStatus extends AgentBriefing {
  letzteAktivitaet: string | null;
  istKuerzlichAktiv: boolean;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function zeitAgo(dateStr: string): string {
  const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  if (diffMin < 1440) return `vor ${Math.floor(diffMin / 60)} Std`;
  if (diffMin < 10080) return `vor ${Math.floor(diffMin / 1440)} Tagen`;
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function getStatusColor(agent: AgentMitStatus): string {
  if (!agent.ist_aktiv) return CI.textLight;
  if (agent.istKuerzlichAktiv) return CI.emerald;
  return CI.amber;
}

function getStatusTitle(agent: AgentMitStatus): string {
  if (!agent.ist_aktiv) return 'Agent inaktiv';
  if (agent.istKuerzlichAktiv) return 'Kürzlich aktiv';
  return 'Aktiv, zuletzt vor längerem';
}

// ─── Agent-Icon-Mapping (kopiert aus Kommandozentrale) ────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  Bot, Brain: Bot, Settings: Bot, Mail: Bot, FileText: Bot,
  Activity: Bot, ClipboardList: Bot, FolderSearch: Bot, Calculator: Bot,
  Car: Bot, Diamond: Bot, Package: Bot, User: Bot,
};

function getAgentIcon(iconName: string): React.ElementType {
  return ICON_MAP[iconName] || Bot;
}

// ─── Komponente: Agenten-Sidebar ──────────────────────────────────────────────

function AgentenSidebar({
  agenten,
  selectedId,
  onSelect,
}: {
  agenten: AgentMitStatus[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      data-testid="agenten-chat-sidebar"
      className="flex flex-col h-full overflow-y-auto"
      style={{ borderRight: `1px solid ${CI.border}` }}
    >
      <div className="px-3 py-2.5 flex-shrink-0" style={{ borderBottom: `1px solid ${CI.borderLight}` }}>
        <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: CI.textMuted }}>
          Agenten
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {agenten.map((agent) => {
          const AgentIcon = getAgentIcon(agent.icon);
          const isSelected = selectedId === agent.agent_id;
          const statusColor = getStatusColor(agent);

          return (
            <button
              key={agent.agent_id}
              data-testid="agent-list-item"
              onClick={() => onSelect(agent.agent_id)}
              className="w-full flex items-center gap-2.5 px-3 py-3 transition-colors text-left"
              style={{
                backgroundColor: isSelected ? CI.redLight : 'transparent',
                borderLeft: isSelected ? `3px solid ${CI.red}` : '3px solid transparent',
              }}
              onMouseEnter={e => {
                if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgHover;
              }}
              onMouseLeave={e => {
                if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 relative"
                style={{ backgroundColor: `${agent.farbe}15` }}
              >
                <AgentIcon className="w-4 h-4" style={{ color: agent.farbe }} />
                <span
                  data-testid="agent-status-dot"
                  className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                  style={{ backgroundColor: statusColor }}
                  title={getStatusTitle(agent)}
                />
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className="text-xs font-medium truncate"
                  style={{ color: isSelected ? CI.redDark : CI.textPrimary }}
                >
                  {agent.agent_name}
                </p>
                {agent.letzteAktivitaet && (
                  <p className="text-[10px] truncate" style={{ color: CI.textMuted }}>
                    {zeitAgo(agent.letzteAktivitaet)}
                  </p>
                )}
              </div>

              {isSelected && (
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: CI.red }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Komponente: Chat-Nachricht (User) ────────────────────────────────────────

function UserNachricht({ nachricht }: { nachricht: ChatNachricht }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%]">
        <div
          className="rounded-2xl rounded-tr-sm px-3.5 py-2.5"
          style={{ backgroundColor: CI.red, color: '#fff' }}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{nachricht.inhalt}</p>
        </div>
        <p className="text-[10px] text-right mt-1" style={{ color: CI.textLight }}>
          {zeitAgo(nachricht.zeitstempel)}
        </p>
      </div>
    </div>
  );
}

// ─── Komponente: Chat-Nachricht (Agent) ──────────────────────────────────────

function AgentenNachricht({
  nachricht,
  agentName,
  agentFarbe,
  onAlsVorschlagSpeichern,
}: {
  nachricht: ChatNachricht;
  agentName: string;
  agentFarbe: string;
  onAlsVorschlagSpeichern: (nachricht: ChatNachricht) => void;
}) {
  const [showAction, setShowAction] = useState(false);

  const StatusIcon = nachricht.erfolgreich === false
    ? XCircle
    : nachricht.aktion?.includes('fehler')
      ? AlertCircle
      : CheckCircle2;

  const statusColor = nachricht.erfolgreich === false ? '#EF4444' : CI.emerald;

  return (
    <div
      className="flex items-start gap-2"
      onMouseEnter={() => setShowAction(true)}
      onMouseLeave={() => setShowAction(false)}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: `${agentFarbe}20` }}
      >
        <Bot className="w-3.5 h-3.5" style={{ color: agentFarbe }} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px] font-semibold" style={{ color: agentFarbe }}>
            {agentName}
          </span>
          <StatusIcon className="w-3 h-3" style={{ color: statusColor }} />
          {nachricht.aktion && (
            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: CI.bgSection, color: CI.textMuted }}>
              {nachricht.aktion.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        <div
          className="rounded-2xl rounded-tl-sm px-3.5 py-2.5"
          style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.border}` }}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: CI.textPrimary }}>
            {nachricht.inhalt}
          </p>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <p className="text-[10px]" style={{ color: CI.textLight }}>
            {zeitAgo(nachricht.zeitstempel)}
          </p>
          {showAction && (
            <button
              onClick={() => onAlsVorschlagSpeichern(nachricht)}
              className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors"
              style={{ color: CI.textMuted, backgroundColor: CI.bgSection }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = CI.redDark; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = CI.textMuted; }}
            >
              <Lightbulb className="w-2.5 h-2.5" />
              Als Vorschlag speichern
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente: AgentenChatTab ─────────────────────────────────────────

interface AgentenChatTabProps {
  agenten: AgentBriefing[];
  protokoll: ProtokollEintrag[];
  onCreateManualVorschlag: (params: {
    titel: string;
    inhalt: string;
    zielAgent?: string;
    kategorie?: string;
    typ?: string;
  }) => Promise<boolean>;
}

export default function AgentenChatTab({
  agenten,
  protokoll,
  onCreateManualVorschlag,
}: AgentenChatTabProps) {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [nachrichten, setNachrichten] = useState<ChatNachricht[]>([]);
  const [eingabe, setEingabe] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Agenten mit Status-Info anreichern ────────────────────────────────────

  const agentenMitStatus: AgentMitStatus[] = agenten.map(agent => {
    const letzteAktivitaet = agent.letzte_ausfuehrung || null;
    const istKuerzlichAktiv = letzteAktivitaet
      ? (Date.now() - new Date(letzteAktivitaet).getTime()) < 5 * 60 * 1000
      : false;

    return { ...agent, letzteAktivitaet, istKuerzlichAktiv };
  });

  // ── Nachrichten für ausgewählten Agenten laden ────────────────────────────

  const ladeNachrichten = useCallback(async (agentId: string) => {
    setLoading(true);
    try {
      const dreiTageZurueck = new Date();
      dreiTageZurueck.setDate(dreiTageZurueck.getDate() - 3);
      const vonDatum = dreiTageZurueck.toISOString();

      // 1. User-Anweisungen (aus agenten_vorschlaege wo erstellt_von=benutzer)
      const { data: userVorschlaege } = await supabase
        .from('agenten_vorschlaege')
        .select('id, titel, beschreibung, erstellt_am, kontext')
        .eq('agent_id', agentId)
        .gte('erstellt_am', vonDatum)
        .order('erstellt_am', { ascending: true });

      const userNachrichten: ChatNachricht[] = (userVorschlaege || [])
        .filter(v => v.kontext?.erstellt_von === 'benutzer' || v.kontext?.typ === 'manuelle_eingabe')
        .map(v => ({
          id: `user-${v.id}`,
          typ: 'user' as const,
          inhalt: v.beschreibung || v.titel || '',
          zeitstempel: v.erstellt_am,
        }));

      // 2. Agenten-Protokoll-Einträge für diesen Agenten
      const agentProtokolle = protokoll
        .filter(p => p.agent_id === agentId)
        .slice(0, 30)
        .map(p => ({
          id: `proto-${p.id}`,
          typ: 'agent' as const,
          inhalt: p.beschreibung || p.aktion || '',
          zeitstempel: p.erstellt_am,
          aktion: p.aktion,
          erfolgreich: p.erfolgreich,
          protokoll_id: p.id,
        }));

      // 3. Chronologisch zusammenführen und sortieren
      const alle = [...userNachrichten, ...agentProtokolle].sort(
        (a, b) => new Date(a.zeitstempel).getTime() - new Date(b.zeitstempel).getTime()
      );

      setNachrichten(alle);
    } catch (err) {
      console.error('Nachrichten laden:', err);
    } finally {
      setLoading(false);
    }
  }, [protokoll]);

  // ── Agenten-Auswahl ───────────────────────────────────────────────────────

  const handleAgentSelect = useCallback((agentId: string) => {
    setSelectedAgentId(agentId);
    setNachrichten([]);
    ladeNachrichten(agentId);
  }, [ladeNachrichten]);

  // Ersten Agenten vorauswählen
  useEffect(() => {
    if (agenten.length > 0 && !selectedAgentId) {
      handleAgentSelect(agenten[0].agent_id);
    }
  }, [agenten, selectedAgentId, handleAgentSelect]);

  // ── Auto-Scroll zum Ende ──────────────────────────────────────────────────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [nachrichten]);

  // ── Nachricht senden ──────────────────────────────────────────────────────

  const handleSend = async () => {
    if (!eingabe.trim() || !selectedAgentId || sending) return;

    const text = eingabe.trim();
    setEingabe('');
    setSending(true);

    // Optimistisches Update
    const tempId = `temp-${Date.now()}`;
    const tempNachricht: ChatNachricht = {
      id: tempId,
      typ: 'user',
      inhalt: text,
      zeitstempel: new Date().toISOString(),
    };
    setNachrichten(prev => [...prev, tempNachricht]);

    try {
      const success = await onCreateManualVorschlag({
        titel: text.length > 80 ? text.slice(0, 80) + '…' : text,
        inhalt: text,
        zielAgent: selectedAgentId,
        kategorie: 'chat_anweisung',
        typ: 'manuell',
      });

      if (!success) {
        // Rollback
        setNachrichten(prev => prev.filter(n => n.id !== tempId));
        toast.error('Anweisung konnte nicht gesendet werden');
      }
    } catch {
      setNachrichten(prev => prev.filter(n => n.id !== tempId));
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── "Als Vorschlag speichern" ─────────────────────────────────────────────

  const handleAlsVorschlagSpeichern = async (nachricht: ChatNachricht) => {
    const success = await onCreateManualVorschlag({
      titel: nachricht.inhalt.length > 80 ? nachricht.inhalt.slice(0, 80) + '…' : nachricht.inhalt,
      inhalt: nachricht.inhalt,
      zielAgent: selectedAgentId || undefined,
      kategorie: 'vorschlag',
      typ: 'vorschlag',
    });

    if (success) {
      toast.success('Als Vorschlag gespeichert');
    } else {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const selectedAgent = agentenMitStatus.find(a => a.agent_id === selectedAgentId);

  // ── Render ────────────────────────────────────────────────────────────────

  if (agenten.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <CircleDot className="w-8 h-8 mx-auto mb-2" style={{ color: CI.textLight }} />
          <p className="text-sm" style={{ color: CI.textMuted }}>Keine Agenten gefunden</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden rounded-xl" style={{ border: `1px solid ${CI.border}`, backgroundColor: CI.bgPanel, minHeight: '460px' }}>

      {/* ── Sidebar: Agenten-Liste ── */}
      <div className="w-[140px] sm:w-[160px] flex-shrink-0">
        <AgentenSidebar
          agenten={agentenMitStatus}
          selectedId={selectedAgentId}
          onSelect={handleAgentSelect}
        />
      </div>

      {/* ── Rechtes Panel: Chat-Fenster ── */}
      <div className="flex-1 flex flex-col min-w-0" data-testid="agenten-chat-window">

        {/* Header */}
        {selectedAgent && (
          <div
            className="flex items-center gap-2.5 px-4 py-3 flex-shrink-0"
            style={{ borderBottom: `1px solid ${CI.border}` }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${selectedAgent.farbe}15` }}
            >
              <Bot className="w-4 h-4" style={{ color: selectedAgent.farbe }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: CI.textPrimary }}>
                {selectedAgent.agent_name}
              </p>
              <p className="text-[10px]" style={{ color: CI.textMuted }}>
                {selectedAgent.ist_aktiv ? (
                  selectedAgent.istKuerzlichAktiv
                    ? '● Kürzlich aktiv'
                    : '● Aktiv'
                ) : '○ Inaktiv'}
              </p>
            </div>
            <button
              onClick={() => selectedAgentId && ladeNachrichten(selectedAgentId)}
              className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
              title="Aktualisieren"
              style={{ color: CI.textMuted }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}

        {/* Nachrichten-Feed */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: CI.textLight }} />
            </div>
          ) : nachrichten.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <Zap className="w-8 h-8 mb-2" style={{ color: CI.textLight }} />
              <p className="text-sm font-medium" style={{ color: CI.textMuted }}>Noch keine Aktivität</p>
              <p className="text-[11px] mt-1" style={{ color: CI.textLight }}>
                Schreibe eine Anweisung an den Agenten
              </p>
            </div>
          ) : (
            nachrichten.map(nachricht =>
              nachricht.typ === 'user' ? (
                <UserNachricht key={nachricht.id} nachricht={nachricht} />
              ) : (
                <AgentenNachricht
                  key={nachricht.id}
                  nachricht={nachricht}
                  agentName={selectedAgent?.agent_name || 'Agent'}
                  agentFarbe={selectedAgent?.farbe || '#6B7280'}
                  onAlsVorschlagSpeichern={handleAlsVorschlagSpeichern}
                />
              )
            )
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Eingabe-Bereich */}
        <div
          className="flex-shrink-0 px-4 py-3"
          style={{ borderTop: `1px solid ${CI.border}` }}
        >
          <div
            className="flex items-end gap-2 rounded-xl px-3 py-2"
            style={{ backgroundColor: CI.bgSection, border: `1px solid ${CI.border}` }}
          >
            <textarea
              data-testid="chat-message-input"
              value={eingabe}
              onChange={e => setEingabe(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Anweisung an ${selectedAgent?.agent_name || 'Agenten'} …`}
              rows={2}
              className="flex-1 bg-transparent text-sm resize-none outline-none leading-relaxed"
              style={{ color: CI.textPrimary, minHeight: '40px', maxHeight: '96px' }}
              disabled={sending}
            />
            <button
              data-testid="chat-send-button"
              onClick={handleSend}
              disabled={!eingabe.trim() || sending}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-shrink-0 mb-0.5"
              style={{
                backgroundColor: eingabe.trim() && !sending ? CI.red : CI.textLight,
                color: '#fff',
              }}
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-[10px] mt-1.5 ml-1" style={{ color: CI.textLight }}>
            Enter zum Senden · Shift+Enter für Zeilenumbruch
          </p>
        </div>
      </div>
    </div>
  );
}
